// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { BeatLoader } from 'react-spinners';
import { type UploadFilesFormUIProps } from '@/types/s3-file';
import { useState } from 'react';
import { FileIcon, RocketIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export function UploadFilesForm({
  isLoading,
  fileInputRef,
  uploadToServer,
  maxFileSize,
  showUpgrade = true,
}: UploadFilesFormUIProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const navigate = useNavigate();

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      uploadToServer(e);
    }
  };

  return (
    <form
      className="flex flex-col items-center justify-center gap-3"
      onSubmit={uploadToServer}
    >
      {showUpgrade && (
        <>
          <p className="text-md">{`${t('UploadFilesForm.fileSizeLimit')} ${maxFileSize} MB`}</p>
          <button className="text-sm text-blue-500"
                  onClick={() => navigate('/channels/@me/boost?msg=Для%20загрузки%20больших%20файлов%20требуется%20обновление%20подписки')}>
            <RocketIcon className="inline-block size-4" />{' '}
            {t('UploadFilesForm.upgradePlan')}
          </button>
        </>
      )}
      {isLoading ? (
        <BeatLoader />
      ) : (
        <div className="flex flex-col gap-5">
          {selectedFile && (
            <div className="mt-4 flex flex-col items-center justify-center">
              {selectedFile.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt={selectedFile.name}
                  className="size-16 rounded-full border object-cover"
                />
              ) : (
                // File icon for non-image files
                <div className="flex size-16 items-center justify-center rounded-full bg-gray-200">
                  <span className="text-gray-600">
                    {selectedFile.name &&
                      selectedFile.name?.split('.')?.pop()?.toUpperCase()}
                  </span>
                </div>
              )}
              <p className="mt-2 text-xs">{selectedFile.name}</p>
            </div>
          )}
          <label
            htmlFor="file"
            className="flex cursor-pointer flex-col items-center justify-center rounded-md border p-2 py-5 transition duration-150 ease-in-out hover:bg-gray-100"
          >
            <FileIcon className="size-8 text-gray-600" />
            <p className="mt-1 text-sm text-gray-600">
              {t('UploadFilesForm.uploadFiles')}
            </p>
            <input
              id="file"
              type="file"
              multiple
              className="hidden"
              required
              ref={fileInputRef}
              onChange={(e) => {
                handleFileChange(e);
              }}
            />
          </label>
        </div>
      )}
    </form>
  );
}
